import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import moment from "moment"

import { Pagination } from "components"
import Table from "components/table"
import Label from "components/forms/label"
import Input from "components/forms/input"
import SelectDescription from "components/forms/select-description"

import useBranchRepositories from "repositories/branch"
import useStoreBalanceStockRepositories from "repositories/store-balance-stock"

const formatNumber = (value) => Number(value ?? 0).toLocaleString("id-ID")

export default function List() {
  const [searchParams, setSearchParams] = useSearchParams()

  const outletId = searchParams.get("outlet_id") || ""
  const month = searchParams.get("month") || moment().format("YYYY-MM")
  const productGroupSearch = searchParams.get("product_group_search") || ""
  const page = searchParams.get("page") || "1"
  const perPage = searchParams.get("per_page") || "20"

  const [searchInput, setSearchInput] = useState(productGroupSearch)

  const isReadyToFetch = !!outletId && !!month

  useEffect(() => {
    setSearchInput(productGroupSearch)
  }, [productGroupSearch])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput === productGroupSearch) return

      const nextParams = new URLSearchParams(searchParams)

      if (searchInput.trim() === "") {
        nextParams.delete("product_group_search")
      } else {
        nextParams.set("product_group_search", searchInput)
      }

      nextParams.set("page", "1")
      setSearchParams(nextParams)
    }, 400)

    return () => clearTimeout(timeoutId)
  }, [searchInput, productGroupSearch, searchParams, setSearchParams])

  const { data: branchSelection, isLoading: isLoadingBranchSelection } =
    useBranchRepositories({ paginate: false })

  const branchOptions = useMemo(() => {
    return Array.isArray(branchSelection) ? branchSelection : []
  }, [branchSelection])

  const { data: reportData, isLoading } = useStoreBalanceStockRepositories({
    outlet_id: outletId || undefined,
    month: month || undefined,
    product_group_search: productGroupSearch || undefined,
    page,
    per_page: perPage,
    enabled: isReadyToFetch,
  })

  const items = useMemo(() => {
    if (Array.isArray(reportData?.data?.data)) return reportData.data.data
    if (Array.isArray(reportData?.data)) return reportData.data
    return reportData?.items?.data ?? []
  }, [reportData])

  const pagination = reportData?.data ?? reportData?.items
  const paginationLinks = useMemo(() => {
    const links = pagination?.links ?? []

    return links.map((link) => {
      if (!link?.url) return link

      const nextParams = new URLSearchParams(searchParams)

      try {
        const url = new URL(link.url, window.location.origin)
        const pageNumber = url.searchParams.get("page")

        if (pageNumber) {
          nextParams.set("page", pageNumber)
        }
      } catch (error) {
        const urlParams = new URLSearchParams(String(link.url).split("?")[1] ?? "")
        const pageNumber = urlParams.get("page")

        if (pageNumber) {
          nextParams.set("page", pageNumber)
        }
      }

      return {
        ...link,
        url: `?${nextParams.toString()}`,
      }
    })
  }, [pagination, searchParams])

  const paginationFrom = useMemo(() => {
    if (pagination?.from) return pagination.from
    if (!pagination || Number(pagination.total ?? 0) === 0) return 0

    return (Number(pagination.current_page ?? 1) - 1) * Number(pagination.per_page ?? perPage) + 1
  }, [pagination, perPage])

  const paginationTo = useMemo(() => {
    if (pagination?.to) return pagination.to
    if (!pagination) return 0

    return Math.min(
      Number(pagination.current_page ?? 1) * Number(pagination.per_page ?? perPage),
      Number(pagination.total ?? 0)
    )
  }, [pagination, perPage])

  const paginationTotal = pagination?.total

  const mergeSearchParams = (nextValues) => {
    const nextParams = new URLSearchParams(searchParams)

    Object.entries(nextValues).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        nextParams.delete(key)
      } else {
        nextParams.set(key, value)
      }
    })

    setSearchParams(nextParams)
  }

  const dataListHeaders = [
    { isAction: false, content: "Product Group" },
    { isAction: false, content: "Product Detail" },
    // { isAction: false, content: "SKU" },
    { isAction: false, content: "Stock Awal" },
    { isAction: false, content: "Stock Masuk" },
    { isAction: false, content: "Stock Keluar" },
    { isAction: false, content: "Stock Akhir" },
  ]

  const dataListColumns = [
    {
      type: "default",
      bold: true,
      content: (v) => (
        <div className="w-[200px] whitespace-normal break-words leading-5">
          {v.product_group_name || "-"}
        </div>
      ),
    },
    {
      type: "default",
      content: (v) => (
        <div className="w-[200px] whitespace-normal break-words leading-5">
          {v.product_unit_name || "-"}
        </div>
      ),
    },
    // {
    //   type: "default",
    //   content: (v) => v.sku || "-",
    // },
    {
      type: "default",
      content: (v) => (
        <span className="font-semibold text-sky-700">
          {formatNumber(v.opening_stock)}
        </span>
      ),
    },
    {
      type: "default",
      content: (v) => (
        <span className="font-semibold text-green-700">
          {formatNumber(v.stock_in)}
        </span>
      ),
    },
    {
      type: "default",
      content: (v) => (
        <span className="font-semibold text-red-700">
          {formatNumber(v.stock_out)}
        </span>
      ),
    },
    {
      type: "default",
      content: (v) => (
        <span className="font-semibold text-neutral-900">
          {formatNumber(v.ending_stock)}
        </span>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <div className="p-5 bg-white border rounded-2xl border-neutral-200">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">
              Store Balance Stock
            </h2>
            <p className="mt-1 text-sm text-neutral-500">
              Menampilkan saldo stock produk per outlet berdasarkan periode bulan.
            </p>
          </div>

          <div className="flex items-end gap-3 flex-wrap">
            <div className="w-56">
              <Label value="Outlet" />
              <SelectDescription
                selection={branchOptions}
                isLoading={isLoadingBranchSelection}
                value={outletId}
                onChange={(value) => {
                  mergeSearchParams({ outlet_id: value, page: "1" })
                }}
                keyValue={(v) => v.id}
                title={(v) => v.name}
                description={(v) => v.code || v.id}
                placeholder="Pilih Outlet..."
              />
            </div>

            <div className="w-44">
              <Label value="Bulan" />
              <Input
                type="month"
                value={month}
                onChange={(e) => {
                  mergeSearchParams({ month: e.target.value, page: "1" })
                }}
              />
            </div>

            <div className="w-72">
              <Label value="Search Product Group" />
              <Input
                type="text"
                value={searchInput}
                placeholder="Cari nama product group..."
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>

            <div className="w-32">
              <Label value="Per Page" />
              <select
                value={perPage}
                onChange={(e) => {
                  mergeSearchParams({ per_page: e.target.value, page: "1" })
                }}
                className="w-full px-3 py-3 text-sm border rounded-xl border-neutral-200 focus:outline-none focus:ring"
              >
                {[10, 20, 50, 100, 200].map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {!isReadyToFetch ? (
        <div className="p-6 bg-white border rounded-2xl border-neutral-200">
          <h3 className="text-base font-semibold text-neutral-900">
            Pilih outlet terlebih dahulu
          </h3>
          <p className="mt-1 text-sm text-neutral-500">
            Data store balance stock tidak akan ditampilkan sebelum outlet dipilih.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
            {[
              {
                label: "Product Units",
                value: formatNumber(reportData?.summary?.total_product_units),
                tone: "text-blue-700",
              },
              {
                label: "Stock Awal",
                value: formatNumber(reportData?.summary?.opening_stock),
                tone: "text-sky-700",
              },
              {
                label: "Stock Masuk",
                value: formatNumber(reportData?.summary?.stock_in),
                tone: "text-green-700",
              },
              {
                label: "Stock Keluar",
                value: formatNumber(reportData?.summary?.stock_out),
                tone: "text-red-700",
              },
              {
                label: "Stock Akhir",
                value: formatNumber(reportData?.summary?.ending_stock),
                tone: "text-neutral-900",
              },
            ].map((card) => (
              <div
                key={card.label}
                className="p-4 border rounded-xl border-neutral-200 bg-neutral-50"
              >
                <div className="text-xs uppercase tracking-wide text-neutral-500">
                  {card.label}
                </div>
                <div className={`mt-2 text-2xl font-semibold ${card.tone}`}>
                  {isLoading ? "..." : card.value}
                </div>
              </div>
            ))}
          </div>

          <div className="p-5 bg-white border rounded-2xl border-neutral-200">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-lg font-semibold text-neutral-900">
                  Balance Stock Detail
                </h2>
                <p className="mt-1 text-sm text-neutral-500">
                  Produk diurutkan A-Z berdasarkan product group lalu product detail.
                </p>
              </div>

              <div className="text-sm text-neutral-500">
                Periode:{" "}
                <span className="font-medium text-neutral-800">
                  {month || "-"}
                </span>
              </div>
            </div>

            <div className="mt-5">
              <Table
                isLoading={isLoading}
                data={items}
                headers={dataListHeaders}
                columns={dataListColumns}
                isEmpty={!items.length}
                isEmptyOnSearching={false}
              />

              <div className="mt-4">

                <Pagination
                  links={paginationLinks}
                  from={paginationFrom}
                  to={paginationTo}
                  total={paginationTotal}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
